import { useEffect } from 'react'
import type { RouteObject } from 'react-router'
import { createBrowserRouter, RouterProvider } from 'react-router'
import TasksScreen from './screens/TasksScreen'
import {
  AuthProvider,
  ErrorScreen,
  LoadingScreen,
  LoginScreen,
  NewsListScreen,
  PrivateOutlet,
  RouteMetadata,
  RoleGuard,
  AccessDeniedScreen,
  SampleChangelogScreen,
  SelectDivisionScreen,
  SelectThemeScreen,
  SettingsScreen,
  CodeScanScreen,
  ThemeProvider,
  TopBar,
  useAutoUpdater,
  ConfirmUpdateModal,
  SampleScreen
} from 'prove-common'
import versionJson from './version.json'
import SampleRegistrationScreen from './screens/SampleRegistrationScreen'
import { BASE_URL } from './constants'
import SamplesScreen from './screens/SamplesScreen'
import { SelectDivisionScreenTopBar } from './components/screen-top-bars/SelectDivisionScreenTopBar'
import { SelectThemeScreenTopBar } from './components/screen-top-bars/SelectThemeScreenTopBar'
import { NewsListScreenTopBar } from './components/screen-top-bars/NewsListScreenTopBar'
import { SampleRegistrationCompletedScreen } from './screens/SampleRegistrationCompletedScreen'
import { SampleMoveScreen } from './screens/SampleMoveScreen'
import { SampleEditScreen } from './screens/SampleEditScreen'
import { SampleEditCompletedScreen } from './screens/SampleEditCompletedScreen'
import { ChangelogScreenTopBar } from './components/screen-top-bars/ChangelogScreenTopBar'
import { HomeScreen } from './screens/HomeScreen'
import { SamplesToProcessScreen } from './screens/SamplesToProcessScreen'
import { TaskScreen } from './screens/TaskScreen'
import { TaskGroupScreen } from './screens/TaskGroupScreen'
import { AppLogo } from './components/app-logo/AppLogo'
import { SideBar } from './components/side-bar/SideBar'
import { SamplesToMarkAsSentScreen } from './screens/SamplesToMarkAsSentScreen'
import { SamplesMarkedAsSentScreen } from './screens/SamplesMarkedAsSentScreen'
import { RedirectRequest } from '@azure/msal-browser'

export const routes: RouteObject[] = [
  {
    element: <RouteMetadata appName='Prøveta' />,
    errorElement: (
      <RouteMetadata appName='Prøveta'>
        <ErrorScreen logo={<AppLogo />} />
      </RouteMetadata>
    ),
    children: [
      {
        path: '/login',
        handle: { title: 'Logg inn' },
        element: (
          <LoginScreen
            logo={<AppLogo />}
            loginButtonText={'Logg inn'}
            homeUrl='/home'
          />
        )
      },
      {
        path: '/unauthorized',
        handle: { title: 'Ingen tilgang' },
        element: <AccessDeniedScreen />
      },
      {
        element: (
          <PrivateOutlet
            homeUrl='/home'
            loadingElement={
              <LoadingScreen
                logo={<AppLogo />}
                loadingMessage='Laster inn...'
              />
            }
            errorElement={<ErrorScreen logo={<AppLogo />} />}
          />
        ),
        path: '/',
        children: [
          {
            element: <RoleGuard requiredRoles={['employee']} />,
            children: [
              {
                path: '/settings',
                handle: { title: 'Innstillinger' },
                element: (
                  <SettingsScreen
                    versionMeta={versionJson}
                    sidebar={<SideBar />}
                    topBar={<TopBar logo={<AppLogo />} title='Innstillinger' />}
                  />
                )
              },
              {
                path: '/settings/select-division',
                handle: { title: 'Velg tilhørighet' },
                element: (
                  <SelectDivisionScreen
                    topBar={<SelectDivisionScreenTopBar />}
                  />
                )
              },
              {
                path: '/settings/select-theme',
                handle: { title: 'Velg tema' },
                element: (
                  <SelectThemeScreen topBar={<SelectThemeScreenTopBar />} />
                )
              },
              {
                path: '/home',
                handle: { title: 'Hjem' },
                element: <HomeScreen />
              },
              {
                path: '/tasks',
                handle: { title: 'Oppgaver' },
                element: <TasksScreen />
              },
              {
                path: '/tasks/:taskId',
                handle: { title: 'Oppgavedetaljer' },
                element: <TaskScreen />
              },
              {
                path: '/task-group',
                handle: { title: 'Oppgavegruppe' },
                element: <TaskGroupScreen />
              },
              {
                path: '/samples',
                handle: { title: 'Prøver' },
                element: <SamplesScreen />
              },
              {
                path: '/samples/:sampleId',
                handle: { title: 'Prøvedetaljer' },
                element: (
                  <SampleScreen
                    logo={<AppLogo />}
                    sideBar={<SideBar />}
                    isProveta={true}
                  />
                )
              },
              {
                path: '/samples/:sampleId/registration',
                handle: { title: 'Registrer prøve' },
                element: <SampleRegistrationScreen />
              },
              {
                path: '/samples/:sampleId/registration-completed',
                handle: { title: 'Prøve registrert' },
                element: <SampleRegistrationCompletedScreen />
              },
              {
                path: '/samples/:sampleId/edit-completed',
                handle: { title: 'Prøve oppdatert' },
                element: <SampleEditCompletedScreen />
              },
              {
                path: '/samples/:sampleId/edit',
                handle: { title: 'Rediger prøve' },
                element: <SampleEditScreen />
              },
              {
                path: '/samples/:sampleId/move',
                handle: { title: 'Flytt prøve' },
                element: <SampleMoveScreen />
              },
              {
                path: '/samples/:sampleId/changelog',
                handle: { title: 'Endringslogg' },
                element: (
                  <SampleChangelogScreen
                    sidebar={<SideBar />}
                    topBar={<ChangelogScreenTopBar />}
                  />
                )
              },
              {
                path: '/newslist',
                handle: { title: 'Nyheter' },
                element: <NewsListScreen topBar={<NewsListScreenTopBar />} />
              },
              {
                path: '/scan',
                handle: { title: 'Les kode' },
                element: <CodeScanScreen appName='Prøveta' />
              },
              {
                path: '/samples-to-process',
                handle: { title: 'Prøver til behandling' },
                element: <SamplesToProcessScreen />
              },
              {
                path: '/samples-to-mark-as-sent',
                handle: { title: 'Prøver som skal sendes' },
                element: <SamplesToMarkAsSentScreen />
              },
              {
                path: '/samples-marked-as-sent',
                handle: { title: 'Sendte prøver' },
                element: <SamplesMarkedAsSentScreen />
              }
            ]
          }
        ]
      }
    ]
  }
]

const browserRouter = createBrowserRouter(routes)

export default function App({
  loginRequest
}: Readonly<{ loginRequest: RedirectRequest }>) {
  const { updateAvailable } = useAutoUpdater(versionJson)
  useEffect(() => {
    if (import.meta.env.MODE === 'test') return

    const _mtm = (globalThis._mtm = globalThis._mtm || [])
    _mtm.push({
      'mtm.startTime': Date.now(),
      event: 'mtm.Start'
    })
    const d = document
    const g = d.createElement('script')
    const s = d.getElementsByTagName('script')[0]
    g.async = true
    g.src =
      'https://cdn.matomo.cloud/mattilsynet.matomo.cloud/container_FsRfhIWC.js'
    s.parentNode?.insertBefore(g, s)
  }, [])
  return (
    <>
      <AuthProvider loginRequest={loginRequest} baseUrl={BASE_URL}>
        <ThemeProvider>
          <RouterProvider router={browserRouter} />
          <ConfirmUpdateModal isOpen={updateAvailable} />
        </ThemeProvider>
      </AuthProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-right' /> */}
    </>
  )
}
